import { useMemo } from 'react'
import { CATEGORIES } from '../db/database'
import { daysUntil, isDueSoon, isOverdue } from '../utils/dateUtils'
import { useLiveTasks, useLiveScadenze } from '../hooks/useSupabaseLiveQuery'
import { useT, useDateFormat } from '../i18n/useT'

export default function RightPanel() {
  const t  = useT()
  const { formatDate } = useDateFormat()
  const tasks    = useLiveTasks()
  const scadenze = useLiveScadenze()

  const stats = useMemo(() => {
    if (!tasks) return { total: 0, soon: 0, overdue: 0, done: 0 }
    return {
      total:   tasks.length,
      soon:    tasks.filter(t => !t.completed && isDueSoon(t.dueDate, 7)).length,
      overdue: tasks.filter(t => !t.completed && isOverdue(t.dueDate)).length,
      done:    tasks.filter(t => t.completed).length
    }
  }, [tasks])

  const upcoming = useMemo(() => {
    if (!tasks) return []
    return tasks
      .filter(t => !t.completed && t.dueDate)
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      .slice(0, 5)
  }, [tasks])

  const nextScadenze = useMemo(() => {
    if (!scadenze) return []
    return [...scadenze]
      .filter(s => daysUntil(s.dueDate) !== null && daysUntil(s.dueDate) >= 0)
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      .slice(0, 3)
  }, [scadenze])

  return (
    <aside className="right-panel">
      {/* Stats */}
      <div className="rp-card">
        <div className="rp-card-title">{t('rp_summary')}</div>
        <div className="rp-stats-grid">
          <div className="rp-stat">
            <span className="rp-stat-num">{stats.total}</span>
            <span className="rp-stat-label">{t('dash_total')}</span>
          </div>
          <div className="rp-stat rp-stat--accent">
            <span className="rp-stat-num">{stats.soon}</span>
            <span className="rp-stat-label">{t('rp_in_deadline')}</span>
          </div>
          <div className="rp-stat rp-stat--danger">
            <span className="rp-stat-num">{stats.overdue}</span>
            <span className="rp-stat-label">{t('rp_overdue')}</span>
          </div>
          <div className="rp-stat rp-stat--done">
            <span className="rp-stat-num">{stats.done}</span>
            <span className="rp-stat-label">{t('rp_completed')}</span>
          </div>
        </div>
      </div>

      {/* Upcoming tasks */}
      <div className="rp-card">
        <div className="rp-card-title">{t('rp_upcoming')}</div>
        {upcoming.length === 0 ? (
          <div className="rp-empty">{t('rp_no_upcoming')}</div>
        ) : (
          <div className="rp-list">
            {upcoming.map(task => {
              const cat   = CATEGORIES[task.category] || CATEGORIES.casa
              const days  = daysUntil(task.dueDate)
              const due   = days === 0 ? t('tasks_today') : days === 1 ? t('tasks_tomorrow') : days < 0 ? t('tasks_days_ago', { n: Math.abs(days) }) : t('tasks_in_days', { n: days })
              const color = days !== null && days <= 7 ? '#E74C3C' : days !== null && days <= 30 ? '#F39C12' : '#27AE60'
              return (
                <div key={task.id} className="rp-item">
                  <span className="rp-item-icon" style={{ background: cat.bg, color: cat.color }}>
                    <cat.Icon size={14} strokeWidth={2.2} />
                  </span>
                  <div className="rp-item-body">
                    <div className="rp-item-title">{task.title}</div>
                    <div className="rp-item-due" style={{ color }}>{due}</div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Recurring deadlines */}
      {nextScadenze.length > 0 && (
        <div className="rp-card">
          <div className="rp-card-title">{t('rp_deadlines')}</div>
          <div className="rp-list">
            {nextScadenze.map(s => {
              const cat  = CATEGORIES[s.category] || CATEGORIES.auto
              const days = daysUntil(s.dueDate)
              const color = days !== null && days <= 30 ? '#E74C3C' : days !== null && days <= 60 ? '#F39C12' : '#27AE60'
              return (
                <div key={s.id} className="rp-item">
                  <span className="rp-item-icon" style={{ background: cat.bg, color: cat.color }}>
                    <cat.Icon size={14} strokeWidth={2.2} />
                  </span>
                  <div className="rp-item-body">
                    <div className="rp-item-title">{s.title}</div>
                    <div className="rp-item-due" style={{ color }}>
                      {days === 0 ? t('scad_today') : `${days}d · ${formatDate(s.dueDate)}`}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    
    </aside>
  )
}
